// Flota de la planta: nomina de la empresa madre, documentos y elegibilidad
// (R1.5 Fase A Unipar, reescrito por ADR-014 rev. 2).
//
// Lo que ve la planta (ADR-014, "Pendiente de validar" 1, default adoptado):
// la nomina de cada empresa madre habilitada, con su codigo de movil, el
// bloqueo de la planta y la elegibilidad para ESA planta, mas los documentos
// de los tipos que ella exige. Nunca el titular ni ids globales: el sujeto de
// un documento se nombra con el id local de la planta. El proveedor y UCORP
// leen las mismas filas con el titular (holder, PP-22); la planta no.
//
// Quien escribe (decision de Federico, ADR-014 §2): nomina y documentos los
// cargan el proveedor (provider_operator, acotado a su empresa madre) y UCORP.
// La planta solo lee, bloquea sus filas y habilita o deshabilita proveedores.
//
// Cada documento es global (uno solo por auto o persona) y vale para todas
// las plantas que lo exigen. Su status (effective, expiring_soon, expired,
// no_expiry) se DERIVA del vencimiento contra la fecha operativa de la planta
// al leer: no se persiste y se calcula en la zona horaria del tenant.
import { z } from "zod";
import { Integer, SiteId, ProviderId, VehicleType } from "./common.js";
import { OperatingDate } from "./ride-requests.js";
import { RequiredDocuments } from "./site-parameters.js";
/**
 * Umbral de aviso: un documento con vencimiento a esta cantidad de dias o
 * menos se reporta expiring_soon. Es parte del contrato (la consola y la torre
 * lo muestran igual), no una policy del tenant.
 */
export const EXPIRY_NOTICE_DAYS = 30;
export const DriverParams = z
    .object({ siteId: SiteId, providerId: ProviderId, driverId: z.string().min(1) })
    .openapi("DriverParams", { description: "Planta, proveedor y chofer (id local de la planta) del path." });
export const VehicleParams = z
    .object({ siteId: SiteId, providerId: ProviderId, vehicleId: z.string().min(1) })
    .openapi("VehicleParams", { description: "Planta, proveedor y vehiculo (id local de la planta) del path." });
export const ProviderParams = z
    .object({ siteId: SiteId, providerId: ProviderId })
    .openapi("ProviderParams", { description: "Planta y proveedor habilitado del path." });
export const DocumentParams = z
    .object({ siteId: SiteId, documentId: z.string().min(1) })
    .openapi("DocumentParams", { description: "Planta y documento del path." });
export const DriverId = z.string().min(1).openapi("DriverId", {
    description: "Identificador local del chofer en la planta (unico por planta y proveedor). Es el que usan los viajes; el mismo chofer tiene otro id en otra planta.",
    example: "driver-01",
});
export const LocalVehicleId = z.string().min(1).openapi("LocalVehicleId", {
    description: "Identificador local del vehiculo en la planta (unico por planta y proveedor). Es el que usan los viajes; el mismo auto tiene otro id en otra planta.",
    example: "combi-01",
});
export const DocumentId = z.string().min(1).openapi("DocumentId", {
    description: "Identificador opaco del documento. Lo genera la API al alta.",
    example: "doc-3f9a1c2b",
});
export const RosterCode = z.string().min(1).openapi("RosterCode", {
    description: "Codigo de movil: identificador de la empresa madre dentro de su nomina, unico en esa nomina y el mismo en todas las plantas (ADR-014).",
    example: "M-14",
});
// --- Titular y tercerizadas de la empresa madre (PP-21, decision D9) ----------
//
// Solo UCORP da de alta organizaciones y tercerizaciones (CLI add-organization
// y add-subcontract, ADR-014 §7). El proveedor no las crea: lee las tercerizadas
// activas de su empresa madre y las usa como titular al dar de alta un auto o
// un conductor. Nada de esto llega a la vista de la planta, que sigue sin
// titular.
export const HolderId = z.string().min(1).openapi("HolderId", {
    description: "Identificador de la organizacion titular (a cuyo nombre esta el auto o para la que trabaja el conductor). Es el id de una tercerizada activa de la empresa madre, tal como lo devuelve GET /sites/{siteId}/providers/{providerId}/subcontractors.",
    example: "org-3f9a1c2b",
});
export const OrganizationKind = z.enum(["company", "individual"]).openapi("OrganizationKind", {
    description: "company: empresa; individual: persona humana con CUIT (monotributista).",
});
export const Subcontractor = z
    .object({
    id: HolderId,
    brandName: z.string().min(1).openapi({ description: "Nombre comercial de la tercerizada.", example: "Remis 10" }),
    kind: OrganizationKind,
})
    .openapi("Subcontractor", {
    description: "Tercerizada activa de la empresa madre, elegible como titular en el alta. No dice para que otras empresas madre trabaja (ADR-014 §7).",
});
export const SubcontractorListResponse = z
    .object({
    providerId: ProviderId,
    subcontractors: z.array(Subcontractor).openapi({
        description: "Tercerizadas con tercerizacion activa y organizacion activa, ordenadas por nombre. No incluye a la empresa madre (titular por defecto, sin holderId).",
    }),
})
    .openapi("SubcontractorListResponse", {
    description: "Respuesta de GET /sites/{siteId}/providers/{providerId}/subcontractors.",
});
/**
 * Titular en las lecturas de nomina (PP-22). Lo reciben solo quien escribe la
 * nomina (provider_operator de ese proveedor) y UCORP; la planta nunca
 * (ADR-014 §7): en sus lecturas el campo esta ausente.
 */
export const HolderRelation = z.enum(["contractor", "subcontractor"]).openapi("HolderRelation", {
    description: "contractor: el titular es la propia empresa madre; subcontractor: una tercerizada suya.",
});
export const RosterHolder = z
    .object({
    id: HolderId.optional().openapi({
        description: "Id de la tercerizada titular, el mismo de listSubcontractors y de holderId en el alta. Ausente cuando el titular es la empresa madre (relation contractor): como en el alta, la empresa madre no se nombra con un id global (ADR-015).",
    }),
    brandName: z.string().min(1).openapi({ description: "Nombre comercial del titular.", example: "Remis 10" }),
    relation: HolderRelation,
})
    .openapi("RosterHolder", {
    description: "Titular de una entrada de la nomina: la empresa madre o una tercerizada suya. No dice para que otras empresas madre trabaja (ADR-014 §7).",
});
const holderView = RosterHolder.optional().openapi({
    description: "Titular de la entrada de nomina. Presente solo para provider_operator de ese proveedor y para UCORP; ausente siempre para los roles de planta (ADR-014 §7).",
});
const holderField = HolderId.optional().openapi({
    description: "Titular: una tercerizada activa de la empresa madre (ver listSubcontractors). Ausente: la empresa madre. Sin tercerizacion activa, o de otra empresa madre, responde 409 subcontract_missing.",
});
// Cambio de titular en la edicion (AT-5b, decision de Federico del 03/10:
// "UCORP puede"). Re-apunta el auto o la relacion global al nuevo titular,
// con sus documentos; la entrada de nomina conserva codigo y estado.
const updateHolderField = HolderId.nullable().openapi({
    description: "Nuevo titular (AT-5b). Ausente: no cambia; null: la empresa madre; un id: una tercerizada activa suya (ver listSubcontractors), si no 409 subcontract_missing. Solo UCORP: provider_operator recibe 403. 409 conflict si el auto o la relacion tambien esta en la nomina de otra empresa madre, o si la persona ya trabaja para el nuevo titular.",
}).optional();
// --- Elegibilidad (ADR-014 §2) -----------------------------------------------
export const ChainInactiveReason = z
    .object({ code: z.literal("chain_inactive") })
    .openapi("ChainInactiveReason", {
    description: "Algun eslabon de la cadena de estados esta inactivo (proveedor, empresa, tercerizacion, nomina, unidad o persona). No dice cual: la planta no ve el titular.",
});
export const BlockedBySiteReason = z
    .object({ code: z.literal("blocked_by_site") })
    .openapi("BlockedBySiteReason", { description: "La planta lo bloqueo (active = false)." });
export const MissingDocumentReason = z
    .object({ code: z.literal("missing_document"), documentType: z.string().min(1) })
    .openapi("MissingDocumentReason", { description: "No tiene ningun documento del tipo que la planta exige." });
export const ExpiredDocumentReason = z
    .object({ code: z.literal("expired_document"), documentType: z.string().min(1) })
    .openapi("ExpiredDocumentReason", {
    description: "Todos sus documentos del tipo exigido estan vencidos a la fecha operativa de la planta.",
});
export const VehicleTooOldReason = z
    .object({
    code: z.literal("vehicle_too_old"),
    modelYear: Integer,
    maxAgeYears: Integer.nonnegative(),
})
    .openapi("VehicleTooOldReason", {
    description: "El auto supera la antiguedad maxima de la planta (anio operativo menos anio del modelo).",
});
export const ModelYearUnknownReason = z
    .object({ code: z.literal("model_year_unknown"), maxAgeYears: Integer.nonnegative() })
    .openapi("ModelYearUnknownReason", {
    description: "La planta pone antiguedad maxima y el auto no tiene anio del modelo cargado.",
});
export const IneligibilityReason = z
    .discriminatedUnion("code", [
    ChainInactiveReason,
    BlockedBySiteReason,
    MissingDocumentReason,
    ExpiredDocumentReason,
    VehicleTooOldReason,
    ModelYearUnknownReason,
])
    .openapi("IneligibilityReason", { description: "Motivo por el que no puede tomar viajes de la planta." });
export const Eligibility = z
    .object({
    eligible: z.boolean(),
    reasons: z.array(IneligibilityReason).openapi({
        description: "Vacio si y solo si eligible. Orden: cadena, bloqueo, documentos, vehiculo.",
    }),
})
    .openapi("Eligibility", {
    description: "Elegibilidad para ESTA planta, calculada al leer (ADR-014 §2): cadena activa, sin bloqueo, documentos exigidos vigentes y condiciones de vehiculo.",
});
const siteActive = z.boolean().openapi({
    description: "Bloqueo de la planta: false cuando la planta lo bloqueo. No borra la entrada de la nomina ni afecta a otras plantas.",
});
// --- Drivers ------------------------------------------------------------------
const updateCode = RosterCode.optional().openapi({
    description: "Nuevo codigo de movil (PP-22). Ausente: no cambia. Unico en la nomina de la empresa madre, bajas incluidas: si choca, 409 conflict.",
});
const driverFields = {
    name: z.string().min(1).openapi({
        description: "Nombre y apellido del chofer, tal como lo informa el prestador.",
        example: "Ruben Acosta",
    }),
    idDocument: z.string().min(1).optional().openapi({
        description: "Identificador que el prestador usa para el chofer (DNI u otro), como texto opaco. Ausente si no lo informo.",
        example: "27123456",
    }),
    phone: z.string().min(1).optional().openapi({
        description: "Telefono de contacto del chofer, si el prestador lo comparte. Ausente si no.",
        example: "+54 291 555 0101",
    }),
};
export const Driver = z
    .object({
    siteId: SiteId,
    providerId: ProviderId,
    id: DriverId,
    code: RosterCode,
    ...driverFields,
    holder: holderView,
    active: siteActive,
    inRoster: z.boolean().openapi({
        description: "false cuando la empresa madre lo dio de baja de su nomina (baja logica, nada se borra). Vuelve a true con la reincorporacion.",
    }),
    eligibility: Eligibility,
})
    .openapi("Driver", {
    description: "Chofer de la nomina de una empresa madre habilitada en la planta, visto desde esa planta. PII minimizada; sin ids globales. El titular (holder) solo lo ven el proveedor de esa nomina y UCORP.",
});
export const CreateDriverRequest = z
    .object({
    providerId: ProviderId,
    id: DriverId.optional().openapi({
        description: "Id local en esta planta (ej: el del manifiesto). Ausente: la API genera uno. En las otras plantas de la empresa madre se genera siempre.",
    }),
    code: RosterCode.optional().openapi({
        description: "Codigo de movil en la nomina. Ausente: se usa el id local.",
    }),
    holderId: holderField,
    ...driverFields,
})
    .openapi("CreateDriverRequest", {
    description: "Alta en la nomina de la empresa madre del proveedor: crea la persona, su relacion con el titular (la empresa madre, o la tercerizada activa de holderId), la entrada de nomina y la fila de cada planta donde la empresa madre esta habilitada. Solo provider_operator de ese proveedor o UCORP.",
});
export const UpdateDriverRequest = z
    .object({
    name: driverFields.name.optional().openapi({
        description: "Nuevo nombre. Ausente: no cambia.",
    }),
    idDocument: z.string().min(1).nullable().optional().openapi({
        description: "Nuevo idDocument opaco. Ausente: no cambia; null: se borra.",
    }),
    phone: z.string().min(1).nullable().optional().openapi({
        description: "Nuevo telefono. Ausente: no cambia; null: se borra.",
    }),
    code: updateCode,
    holderId: updateHolderField,
})
    .openapi("UpdateDriverRequest", {
    description: "Edicion parcial de los datos del chofer, de su codigo de movil y, solo UCORP, de su titular (valen en todas las plantas). Politica de ausencia: campo ausente no cambia; null borra el valor (en holderId, vuelve a la empresa madre).",
});
export const ListDriversQuery = z
    .object({
    providerId: ProviderId.optional().openapi({
        description: "Limita el listado a un proveedor. Ausente: todos los de la planta (o el del usuario, si es provider_operator).",
    }),
    includeInactive: z.enum(["true", "false"]).optional().openapi({
        description: "true incluye los dados de baja de la nomina y los bloqueados por la planta. Ausente o false: solo los que estan en la nomina y sin bloqueo.",
    }),
})
    .openapi("ListDriversQuery", { description: "Filtros del listado de choferes." });
export const DriverListResponse = z
    .object({
    today: OperatingDate.openapi({
        description: "Fecha operativa de la planta con la que se calculo la elegibilidad.",
    }),
    drivers: z.array(Driver).openapi({
        description: "Choferes de la planta (o del proveedor del usuario), ordenados por proveedor y nombre.",
    }),
})
    .openapi("DriverListResponse", { description: "Respuesta de GET /sites/{siteId}/drivers." });
// --- Vehiculos de la planta -----------------------------------------------------
const vehicleFields = {
    type: VehicleType,
    capacity: Integer.positive().openapi({ description: "Pasajeros sentados, sin contar al chofer." }),
    plate: z.string().min(1).openapi({
        description: "Patente. Unica entre las unidades activas de todo el sistema.",
        example: "AA100AA",
    }),
    year: Integer.min(1950).max(2100).optional().openapi({
        description: "Anio del modelo, para las condiciones de antiguedad. Ausente: no cargado.",
        example: 2021,
    }),
};
export const SiteVehicle = z
    .object({
    siteId: SiteId,
    providerId: ProviderId,
    id: LocalVehicleId,
    code: RosterCode,
    ...vehicleFields,
    holder: holderView,
    active: siteActive,
    inRoster: z.boolean().openapi({
        description: "false cuando la empresa madre lo dio de baja de su nomina (baja logica, nada se borra). Vuelve a true con la reincorporacion.",
    }),
    eligibility: Eligibility,
})
    .openapi("SiteVehicle", {
    description: "Vehiculo de la nomina de una empresa madre habilitada en la planta, visto desde esa planta, con su elegibilidad. Sin ids globales. El titular (holder) solo lo ven el proveedor de esa nomina y UCORP.",
});
export const CreateVehicleRequest = z
    .object({
    providerId: ProviderId,
    id: LocalVehicleId.optional().openapi({
        description: "Id local en esta planta. Ausente: la API genera uno. En las otras plantas de la empresa madre se genera siempre.",
    }),
    code: RosterCode.optional().openapi({
        description: "Codigo de movil en la nomina. Ausente: se usa el id local.",
    }),
    holderId: holderField,
    ...vehicleFields,
})
    .openapi("CreateVehicleRequest", {
    description: "Alta de un auto (titular: la empresa madre, o la tercerizada activa de holderId) en la nomina y en cada planta donde la empresa madre esta habilitada. Solo provider_operator de ese proveedor o UCORP. 409 si la patente ya esta activa.",
});
export const UpdateVehicleRequest = z
    .object({
    type: VehicleType.optional().openapi({ description: "Nuevo tipo. Ausente: no cambia." }),
    capacity: vehicleFields.capacity.optional().openapi({ description: "Nueva capacidad. Ausente: no cambia." }),
    plate: vehicleFields.plate.optional().openapi({ description: "Nueva patente. Ausente: no cambia." }),
    year: Integer.min(1950).max(2100).nullable().optional().openapi({
        description: "Nuevo anio del modelo. Ausente: no cambia; null: se borra.",
    }),
    code: updateCode,
    holderId: updateHolderField,
})
    .openapi("UpdateVehicleRequest", {
    description: "Edicion parcial del auto, de su codigo de movil y, solo UCORP, de su titular (vale en todas las plantas). Campo ausente no cambia; null borra (en holderId, vuelve a la empresa madre).",
});
export const ListVehiclesQuery = z
    .object({
    providerId: ProviderId.optional().openapi({
        description: "Limita el listado a un proveedor. Ausente: todos los de la planta (o el del usuario, si es provider_operator).",
    }),
    includeInactive: z.enum(["true", "false"]).optional().openapi({
        description: "true incluye los dados de baja de la nomina y los bloqueados por la planta. Ausente o false: solo los que estan en la nomina y sin bloqueo.",
    }),
})
    .openapi("ListVehiclesQuery", { description: "Filtros del listado de vehiculos." });
export const VehicleListResponse = z
    .object({
    today: OperatingDate.openapi({
        description: "Fecha operativa de la planta con la que se calculo la elegibilidad.",
    }),
    vehicles: z.array(SiteVehicle).openapi({
        description: "Vehiculos de la planta (o del proveedor del usuario), ordenados por proveedor e id local.",
    }),
})
    .openapi("VehicleListResponse", { description: "Respuesta de GET /sites/{siteId}/vehicles." });
// --- Bloqueo de la planta y habilitacion de proveedores ------------------------
export const SetSiteActiveRequest = z
    .object({
    active: z.boolean().openapi({ description: "false bloquea en esta planta; true desbloquea." }),
})
    .openapi("SetSiteActiveRequest", {
    description: "Bloqueo o desbloqueo de la planta sobre un chofer, un vehiculo o un proveedor habilitado. Solo roles de planta o UCORP; no toca la nomina ni otras plantas.",
});
export const SiteProviderStatus = z
    .object({
    siteId: SiteId,
    providerId: ProviderId,
    brandName: z.string().min(1),
    active: z.boolean().openapi({
        description: "false cuando la planta deshabilito al proveedor: su nomina deja de ser elegible en esta planta.",
    }),
})
    .openapi("SiteProviderStatus", { description: "Estado de la habilitacion de un proveedor en la planta." });
// --- Documents ------------------------------------------------------------------
export const DocumentSubjectType = z.enum(["vehicle", "driver"]).openapi("DocumentSubjectType", {
    description: "A que pertenece el documento: al auto o a la persona que maneja.",
});
export const DocumentSubject = z
    .object({
    type: DocumentSubjectType,
    providerId: ProviderId,
    id: z.string().min(1).openapi({
        description: "Id local del vehiculo o del chofer en esta planta.",
        example: "combi-01",
    }),
})
    .openapi("DocumentSubject", {
    description: "Sujeto de un documento nombrado desde la planta: un vehiculo o un chofer de un proveedor habilitado. El documento pertenece al auto o a la persona y vale en todas sus plantas.",
});
export const DocumentStatus = z
    .enum(["effective", "expiring_soon", "expired", "no_expiry"])
    .openapi("DocumentStatus", {
    description: "Estado derivado del vencimiento contra la fecha operativa de la planta: effective (vence en mas de 30 dias), expiring_soon (vence en 30 dias o menos, hoy inclusive), expired (la fecha ya paso) o no_expiry (el documento no vence).",
});
const documentFields = {
    type: z.string().min(1).openapi({
        description: "Tipo de documento (catalogo abierto): habilitacion_municipal, seguro, vtv, licencia, psicofisico... Cada planta exige los suyos (requiredDocuments).",
        example: "vtv",
    }),
    expiresAt: OperatingDate.nullable().openapi({
        description: "Fecha de vencimiento (calendario). null: el documento no vence.",
    }),
    reference: z.string().min(1).optional().openapi({
        description: "Numero o reference del documento, sin el archivo en si. Ausente si no se cargo.",
        example: "POL-2026-00812",
    }),
};
export const Document = z
    .object({
    siteId: SiteId,
    id: DocumentId,
    subject: DocumentSubject,
    ...documentFields,
    status: DocumentStatus,
})
    .openapi("Document", {
    description: "Documento de un vehiculo o chofer con su vencimiento y el estado derivado a la fecha operativa de la planta. Los roles de planta solo ven los tipos que la planta exige.",
});
export const CreateDocumentRequest = z
    .object({
    subject: DocumentSubject,
    ...documentFields,
})
    .openapi("CreateDocumentRequest", {
    description: "Alta de un documento, una sola vez, en el auto o la persona (ADR-014 §2): vale en todas las plantas que exigen ese tipo. Solo provider_operator (sujetos de su empresa madre) o UCORP.",
});
export const UpdateDocumentRequest = z
    .object({
    type: documentFields.type.optional().openapi({
        description: "Nuevo tipo. Ausente: no cambia.",
    }),
    expiresAt: OperatingDate.nullable().openapi({
        description: "Nuevo vencimiento. Ausente: no cambia; null: el documento pasa a no vencer.",
    }).optional(),
    reference: z.string().min(1).nullable().optional().openapi({
        description: "Nueva reference. Ausente: no cambia; null: se borra.",
    }),
})
    .openapi("UpdateDocumentRequest", {
    description: "Edicion parcial de un documento (renovacion tipica: nuevo vencimiento y reference). Campo ausente no cambia; null borra. Solo provider_operator o UCORP.",
});
export const ListDocumentsQuery = z
    .object({
    status: DocumentStatus.optional().openapi({
        description: "Solo los documentos en este estado. Ausente: todos.",
    }),
    subjectType: DocumentSubjectType.optional().openapi({
        description: "Solo documentos de vehiculos o solo de choferes. Ausente: ambos.",
    }),
    providerId: ProviderId.optional().openapi({
        description: "Solo documentos de sujetos de este proveedor.",
    }),
    subjectId: z.string().min(1).optional().openapi({
        description: "Solo documentos de este vehiculo o chofer (id local; combinar con providerId y subjectType).",
    }),
})
    .openapi("ListDocumentsQuery", { description: "Filtros del listado de documentos, por estado y por sujeto." });
export const DocumentListResponse = z
    .object({
    today: OperatingDate.openapi({
        description: "Fecha operativa de la planta con la que se derivaron los estados.",
    }),
    documents: z.array(Document).openapi({
        description: "Documentos que cumplen los filtros, los vencidos primero y luego por vencimiento ascendente.",
    }),
    requiredDocuments: RequiredDocuments.optional().openapi({
        description: "Tipos que la planta exige (PP-23, 0.6.40): el proveedor no lee los parametros de la planta y los necesita para saber que le falta y a que planta afecta un vencimiento. Ausente: API anterior a 0.6.40.",
    }),
})
    .openapi("DocumentListResponse", { description: "Respuesta de GET /sites/{siteId}/documents." });
export const CountByDocumentStatus = z
    .object({
    effective: z.number().int().nonnegative().openapi({ format: "int32" }),
    expiring_soon: z.number().int().nonnegative().openapi({ format: "int32" }),
    expired: z.number().int().nonnegative().openapi({ format: "int32" }),
    no_expiry: z.number().int().nonnegative().openapi({ format: "int32" }),
})
    .openapi("CountByDocumentStatus", { description: "Cantidad de documentos por estado derivado." });
export const ProviderExpirySummary = z
    .object({
    providerId: ProviderId,
    count: CountByDocumentStatus,
})
    .openapi("ProviderExpirySummary", { description: "Conteo de documentos por estado para un proveedor." });
export const ExpirySummaryResponse = z
    .object({
    today: OperatingDate.openapi({
        description: "Fecha operativa de la planta con la que se derivaron los estados.",
    }),
    noticeDays: z.number().int().positive().openapi({
        format: "int32",
        description: "Umbral de dias del estado expiring_soon (EXPIRY_NOTICE_DAYS).",
    }),
    total: CountByDocumentStatus,
    byProvider: z.array(ProviderExpirySummary).openapi({
        description: "Conteos por proveedor, ordenados por id de proveedor. Solo el propio si el usuario es provider_operator.",
    }),
})
    .openapi("ExpirySummaryResponse", {
    description: "Resumen de vencimientos de la planta: la cabecera de la consola y el insumo de la alerta de Fase B.",
});
//# sourceMappingURL=drivers.js.map